"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CarbonWallet = void 0;
const CarbonSDK_1 = require("../CarbonSDK");
const codec_1 = require("../codec");
const auth_1 = require("../codec/cosmos/auth/v1beta1/auth");
const tx_1 = require("../codec/cosmos/authz/v1beta1/tx");
const web3_1 = require("../codec/ethermint/types/v1/web3");
const constant_1 = require("../constant");
const grant_1 = require("../constant/grant");
const walletProvider_1 = require("../constant/walletProvider");
const provider_1 = require("../provider");
const util_1 = require("../util");
const address_1 = require("../util/address");
const blockchain_1 = require("../util/blockchain");
const ethermint_1 = require("../util/ethermint");
const fetch_1 = require("../util/fetch");
const generic_1 = require("../util/generic");
const number_1 = require("../util/number");
const tx_2 = require("../util/tx");
const amino_1 = require("@cosmjs/amino");
const proto_signing_1 = require("@cosmjs/proto-signing");
const stargate_1 = require("@cosmjs/stargate");
const tendermint_rpc_1 = require("@cosmjs/tendermint-rpc");
const responses_1 = require("@cosmjs/tendermint-rpc/build/tendermint37/responses");
const utils_1 = require("@cosmjs/utils");
const tx_3 = require("cosmjs-types/cosmos/tx/v1beta1/tx");
const dayjs_1 = __importDefault(require("dayjs"));
const utc_1 = __importDefault(require("dayjs/plugin/utc"));
const CarbonSigner_1 = require("./CarbonSigner");
const CarbonSigningClient_1 = require("./CarbonSigningClient");
dayjs_1.default.extend(utc_1.default);
class CarbonWallet {
    constructor(opts) {
        var _a, _b, _c, _d, _e;
        this.initialized = false;
        this.sequenceInvalidated = false;
        this.isAccountNotFoundError = (error, address) => {
            var _a;
            return (_a = error.message) === null || _a === void 0 ? void 0 : _a.includes(`account ${address} not found`);
        };
        this.isTxHashNotFound = (error, hash) => {
            var _a;
            return (_a = error.message) === null || _a === void 0 ? void 0 : _a.includes(`tx (${hash}) not found`);
        };
        this.isNonceMismatchError = (error) => {
            const errorMessage = 'account sequence mismatch';
            const includes = error === null || error === void 0 ? void 0 : error.message.includes(errorMessage);
            if (includes) {
                return {
                    message: error === null || error === void 0 ? void 0 : error.message,
                };
            }
            return false;
        };
        const network = (_a = opts.network) !== null && _a !== void 0 ? _a : constant_1.DEFAULT_NETWORK;
        this.network = network;
        this.txDefaultBroadCastMode = opts.txDefaultBroadcastMode;
        this.networkConfig = constant_1.NetworkConfigs[network];
        this.configOverride = (_b = opts.config) !== null && _b !== void 0 ? _b : {};
        this.providerAgent = opts.providerAgent;
        this.defaultTimeoutBlocks = (_c = opts.defaultTimeoutBlocks) !== null && _c !== void 0 ? _c : 30; // default ~1 minute
        this.disableRetryOnSequenceError = (_d = opts.disableRetryOnSequenceError) !== null && _d !== void 0 ? _d : false;
        this.triggerMerge = (_e = opts.triggerMerge) !== null && _e !== void 0 ? _e : false;
        this.gasFee = opts.gasFee;
        this.updateNetwork(network);
        this.onRequestSign = opts.onRequestSign;
        this.onSignComplete = opts.onSignComplete;
        this.onBroadcastTxSuccess = opts.onBroadcastTxSuccess;
        this.onBroadcastTxFail = opts.onBroadcastTxFail;
        this.txDispatchManager = new generic_1.QueueManager(this.dispatchTx.bind(this));
        this.txSignManager = new generic_1.QueueManager(this.signTx.bind(this));
        this.mnemonic = opts.mnemonic;
        if (this.mnemonic) {
            this.privateKey = util_1.AddressUtils.SWTHAddress.mnemonicToPrivateKey(this.mnemonic);
        }
        else if (opts.privateKey) {
            this.privateKey = util_1.AddressUtils.stringOrBufferToBuffer(opts.privateKey);
        }
        const addressOpts = Object.assign({ network }, (this.configOverride.Bech32Prefix && {
            bech32Prefix: this.configOverride.Bech32Prefix,
        }));
        if (opts.signer) {
            this.signer = opts.signer;
            this.publicKey = Buffer.from(opts.publicKeyBase64, "base64");
            this.bech32Address = util_1.AddressUtils.SWTHAddress.publicKeyToAddress(this.publicKey, addressOpts);
        }
        else if (this.privateKey) {
            this.publicKey = util_1.AddressUtils.SWTHAddress.privateToPublicKey(this.privateKey);
            this.bech32Address = util_1.AddressUtils.SWTHAddress.publicKeyToAddress(this.publicKey, addressOpts);
            let prefix = addressOpts.bech32Prefix;
            if (!addressOpts.bech32Prefix)
                prefix = address_1.SWTHAddress.getBech32Prefix(addressOpts === null || addressOpts === void 0 ? void 0 : addressOpts.network, addressOpts === null || addressOpts === void 0 ? void 0 : addressOpts.bech32Prefix);
            if (!prefix)
                throw new Error("cannot instantiate wallet signer, no prefix");
            this.signer = new CarbonSigner_1.CarbonPrivateKeySigner(this.privateKey, prefix);
        }
        else if (opts.bech32Address) {
            // read-only wallet, without private/public keys
            this.signer = new CarbonSigner_1.CarbonNonSigner();
            this.publicKey = Buffer.from([]);
            this.bech32Address = opts.bech32Address;
        }
        else {
            throw new Error("cannot instantiate wallet signer");
        }
        const addressBytes = util_1.AddressUtils.SWTHAddress.getAddressBytes(this.bech32Address, this.network);
        this.hexAddress = `0x${Buffer.from(addressBytes).toString("hex")}`;
        this.evmHexAddress = opts.bech32Address ? '' : util_1.AddressUtils.ETHAddress.publicKeyToAddress(this.publicKey, addressOpts);
        this.evmBech32Address = opts.bech32Address ? '' : util_1.AddressUtils.ETHAddress.publicKeyToBech32Address(this.publicKey, addressOpts);
    }
    static withPrivateKey(privateKey, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { privateKey }));
    }
    static withMnemonic(mnemonic, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { mnemonic }));
    }
    static withLedger(cosmosLedger, publicKeyBase64, opts = {}) {
        const signer = new CarbonSigner_1.CarbonLedgerSigner(cosmosLedger);
        const wallet = CarbonWallet.withSigner(signer, publicKeyBase64, Object.assign({ providerAgent: walletProvider_1.ProviderAgent.Ledger }, opts));
        return wallet;
    }
    static withSigner(signer, publicKeyBase64, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { signer,
            publicKeyBase64 }));
    }
    static withKeplr(keplr, chainInfo, keplrKey, opts = {}) {
        const signer = keplrKey.isNanoLedger ? provider_1.KeplrAccount.createKeplrSignerAmino(keplr, chainInfo, keplrKey) : provider_1.KeplrAccount.createKeplrSigner(keplr, chainInfo, keplrKey);
        const publicKeyBase64 = Buffer.from(keplrKey.pubKey).toString("base64");
        const wallet = CarbonWallet.withSigner(signer, publicKeyBase64, Object.assign({ providerAgent: walletProvider_1.ProviderAgent.KeplrExtension }, opts));
        return wallet;
    }
    static withLeap(leap, chainId, leapKey, opts = {}) {
        const signer = leapKey.isNanoLedger ? provider_1.LeapAccount.createLeapSignerAmino(leap, chainId) : provider_1.LeapAccount.createLeapSigner(leap, chainId);
        const publicKeyBase64 = Buffer.from(leapKey.pubKey).toString("base64");
        const wallet = CarbonWallet.withSigner(signer, publicKeyBase64, Object.assign({ providerAgent: walletProvider_1.ProviderAgent.LeapExtension }, opts));
        return wallet;
    }
    static withMetamask(metamask, evmChainId, compressedPubKeyBase64, addressOptions, opts = {}) {
        const signer = provider_1.MetaMask.createMetamaskSigner(metamask, evmChainId, compressedPubKeyBase64, addressOptions);
        const wallet = CarbonWallet.withSigner(signer, compressedPubKeyBase64, Object.assign({ providerAgent: walletProvider_1.ProviderAgent.MetamaskExtension }, opts));
        return wallet;
    }
    static withAddress(bech32Address, opts = {}) {
        return new CarbonWallet(Object.assign(Object.assign({}, opts), { bech32Address }));
    }
    initialize(queryClient, gasFee, fallbackConfig = null) {
        return __awaiter(this, void 0, void 0, function* () {
            this.query = queryClient;
            this.gasFee = gasFee;
            const promises = [
                this.reloadAccountSequence(),
                this.reloadMergeAccountStatus(),
            ];
            if (!this.tmClient)
                promises.push(this.reconnectTmClient(fallbackConfig));
            yield Promise.all(promises);
            this.initialized = true;
            return this;
        });
    }
    setGrantee(grantee) {
        this.grantee = grantee;
    }
    isGranteeValid() {
        if (!this.grantee)
            return false;
        const { expiry, authMsgsVersion } = this.grantee;
        const hasNotExpired = dayjs_1.default.utc(expiry).isAfter(dayjs_1.default.utc().add(grant_1.BUFFER_PERIOD, 'seconds'));
        const versionUpToDate = authMsgsVersion === this.authorizedMsgsVersion;
        return hasNotExpired && versionUpToDate && !!this.grantee.signer;
    }
    updateNetwork(network) {
        this.network = network;
        this.networkConfig = util_1.GenericUtils.overrideConfig(constant_1.NetworkConfigs[network], this.configOverride);
        delete this.signingClient;
        return this;
    }
    setAuthorizedMsgs(msgs, version) {
        this.authorizedMsgs = msgs;
        this.authorizedMsgsVersion = version;
    }
    getSignedTx(signerAddress, messages, sequence, opts, granterAddress) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            const { memo = "", accountNumber, explicitSignerData, feeDenom } = opts;
            const signingClient = yield this.getSigningClient();
            const [account] = yield this.signer.getAccounts();
            let signature = null;
            const evmChainId = this.evmChainId;
            try {
                yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onRequestSign) === null || _a === void 0 ? void 0 : _a.call(this, messages); });
                yield this.checkWalletSignatureCompatibility();
                const signerData = Object.assign(Object.assign({ accountNumber: accountNumber !== null && accountNumber !== void 0 ? accountNumber : this.accountInfo.accountNumber, chainId: this.getChainId(), sequence }, explicitSignerData), { evmChainId });
                const fee = (_a = opts === null || opts === void 0 ? void 0 : opts.fee) !== null && _a !== void 0 ? _a : this.estimateTxFee(messages, feeDenom);
                const txRaw = yield signingClient.sign(signerAddress, messages, fee, memo, signerData, granterAddress);
                let sig;
                if (CarbonSigner_1.isCarbonEIP712Signer(this.signer)) {
                    if (this.signer.legacyEip712SignMode) {
                        const feePayerSigBz = web3_1.ExtensionOptionsWeb3Tx.decode(tx_3.TxBody.decode(txRaw.bodyBytes).extensionOptions[0].value).feePayerSig;
                        sig = Uint8Array.from(Buffer.from(feePayerSigBz.slice(0, -1)));
                    }
                    else {
                        sig = txRaw.signatures[0];
                    }
                    signature = amino_1.encodeSecp256k1Signature(account.pubkey, sig);
                    signature = Object.assign(Object.assign({}, signature), { pub_key: Object.assign(Object.assign({}, signature.pub_key), { type: ethermint_1.ETH_SECP256K1_TYPE }) });
                    return txRaw;
                }
                signature = amino_1.encodeSecp256k1Signature(account.pubkey, txRaw.signatures[0]);
                return txRaw;
            }
            finally {
                yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onSignComplete) === null || _a === void 0 ? void 0 : _a.call(this, signature); });
            }
        });
    }
    /**
    * Non EVM wallets (Keplr, Leap, Ledger, Legacy Metamask, Encrypted Key) current mode of signing
    * does not support submiting transaction with only eth accounts.
    * This method assumes that if a transaction is carried out by a non evm wallet with only funds in eth address
    * as submitting the msg with an eth address as the signer because it is impossible to get the signer field at this stage.
    *
    * Keplr technically can support submiting transaction with only eth accounts via EIP-712 eth signature but is blocked by
    * keplr wallet extension due to incompability of carbon's chain-id. Keplr requires EIP-712 signing chains to have a chain id of {chainname_XXXX-X}
    */
    checkWalletSignatureCompatibility() {
        return __awaiter(this, void 0, void 0, function* () {
            const query = this.getQueryClient();
            const hasCarbonBalances = (yield query.bank.AllBalances({ address: this.bech32Address, resolveDenom: false })).balances.length > 0;
            const hasEvmAddressBalances = (yield query.bank.AllBalances({ address: this.evmBech32Address, resolveDenom: false })).balances.length > 0;
            const isEvmWallet = this.isEvmWallet();
            if (hasEvmAddressBalances && !hasCarbonBalances && !isEvmWallet) {
                this.sequenceInvalidated = true;
                throw new Error('Transaction is not allowed from a non-evm wallet for an account with only funds in evm address');
            }
        });
    }
    /**
     * broadcast TX and wait for block confirmation
     *
     */
    broadcastTx(txRaw, opts = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const { pollIntervalMs = 3000, timeoutMs = 60000 } = opts;
            const tx = CarbonWallet.TxRaw.encode(txRaw).finish();
            const carbonClient = yield this.getSigningClient();
            const response = yield carbonClient.broadcastTx(tx, timeoutMs, pollIntervalMs);
            if (stargate_1.isDeliverTxFailure(response)) {
                // tx failed
                throw new tx_2.CarbonCustomError(`[${response.code}] ${response.rawLog}`, tx_2.ErrorType.BLOCK_FAIL, response);
            }
            return response;
        });
    }
    /**
     * broadcast TX to mempool but doesnt wait for block confirmation
     *
     */
    broadcastTxToMempoolWithoutConfirm(txRaw) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = CarbonWallet.TxRaw.encode(txRaw).finish();
            const tmClient = yield this.getTmClient();
            const response = yield tmClient.broadcastTxSync({ tx });
            if (!responses_1.broadcastTxSyncSuccess(response)) {
                // tx failed
                throw new tx_2.CarbonCustomError(`[${response.code}] ${response.log}`, tx_2.ErrorType.BROADCAST_FAIL, response);
            }
            return response;
        });
    }
    /**
     * broadcast TX but doesnt wait for block confirmation nor submission to mempool
     *
     */
    broadcastTxWithoutConfirm(txRaw) {
        return __awaiter(this, void 0, void 0, function* () {
            const tx = CarbonWallet.TxRaw.encode(txRaw).finish();
            const tmClient = yield this.getTmClient();
            return tmClient.broadcastTxAsync({ tx });
        });
    }
    signAndBroadcast(messages, signOpts, broadcastOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const promise = new Promise((resolve, reject) => {
                this.txSignManager.enqueue({
                    signerAddress: this.bech32Address,
                    messages,
                    broadcastOpts,
                    signOpts,
                    handler: { resolve, reject },
                });
            });
            return promise;
        });
    }
    signTx(txRequest) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const { reattempts, signerAddress, signOpts, messages, broadcastOpts, handler: { resolve, reject }, } = txRequest;
            const isAuthorized = messages.every((message) => { var _a; return (_a = this.authorizedMsgs) === null || _a === void 0 ? void 0 : _a.includes(message.typeUrl); });
            if (this.isGranteeValid() && isAuthorized) {
                yield this.signWithGrantee(txRequest);
            }
            else {
                try {
                    if (!this.accountInfo
                        || ((_a = this.accountInfo) === null || _a === void 0 ? void 0 : _a.address) === this.evmBech32Address // refresh to check if carbon acc is present
                        || this.sequenceInvalidated)
                        yield this.reloadAccountSequence();
                    let timeoutHeight;
                    // timeoutHeight is not supported for EIP-712
                    if (!CarbonSigner_1.isCarbonEIP712Signer(this.signer)) {
                        timeoutHeight = yield this.getTimeoutHeight();
                    }
                    let sequence;
                    if (!this.accountInfo) {
                        sequence = (_b = signOpts === null || signOpts === void 0 ? void 0 : signOpts.sequence) !== null && _b !== void 0 ? _b : 0;
                    }
                    else {
                        sequence = this.accountInfo.sequence;
                        this.accountInfo = Object.assign(Object.assign({}, this.accountInfo), { sequence: sequence + 1 });
                    }
                    const _signOpts = Object.assign(Object.assign({}, signOpts), { explicitSignerData: Object.assign({ timeoutHeight }, signOpts === null || signOpts === void 0 ? void 0 : signOpts.explicitSignerData) });
                    const signedTx = yield this.getSignedTx(signerAddress, messages, sequence, _signOpts);
                    this.txDispatchManager.enqueue({
                        reattempts,
                        signerAddress,
                        messages,
                        signedTx,
                        broadcastOpts,
                        signOpts: _signOpts,
                        handler: { resolve, reject, requestId: `${sequence}` },
                    });
                }
                catch (error) {
                    reject(error);
                }
            }
        });
    }
    signWithGrantee(txRequest) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function* () {
            const { reattempts, signOpts, messages, handler: { resolve, reject }, } = txRequest;
            try {
                if (!this.grantee) {
                    throw new Error("grantee not set");
                }
                const signer = this.grantee.signer;
                const accounts = yield signer.getAccounts();
                if (!accounts.length) {
                    throw Error("cannot retrieve grantee accounts");
                }
                const granteeAddress = accounts[0].address;
                const accountInfo = yield this.getAccount(granteeAddress);
                if (!accountInfo) {
                    throw new Error("grantee account not initialized");
                }
                const msgs = messages.map((message) => codec_1.registry.encodeAsAny(Object.assign({}, message)));
                const msgExecMessages = [{
                        typeUrl: util_1.CarbonTx.Types.MsgExec,
                        value: tx_1.MsgExec.fromPartial({
                            grantee: granteeAddress,
                            msgs,
                        }),
                    }];
                const timeoutHeight = yield this.getTimeoutHeight();
                const fee = (_a = signOpts === null || signOpts === void 0 ? void 0 : signOpts.fee) !== null && _a !== void 0 ? _a : this.estimateTxFee(msgExecMessages, signOpts === null || signOpts === void 0 ? void 0 : signOpts.feeDenom);
                const memo = (_b = signOpts === null || signOpts === void 0 ? void 0 : signOpts.memo) !== null && _b !== void 0 ? _b : "";
                const sequence = (_c = signOpts === null || signOpts === void 0 ? void 0 : signOpts.sequence) !== null && _c !== void 0 ? _c : accountInfo.sequence;
                const accountNumber = (_d = signOpts === null || signOpts === void 0 ? void 0 : signOpts.accountNumber) !== null && _d !== void 0 ? _d : accountInfo.accountNumber;
                const _signOpts = Object.assign(Object.assign({}, signOpts), { explicitSignerData: Object.assign({ timeoutHeight }, signOpts === null || signOpts === void 0 ? void 0 : signOpts.explicitSignerData) });
                const signerData = Object.assign({ accountNumber, chainId: this.getChainId(), sequence }, signOpts === null || signOpts === void 0 ? void 0 : signOpts.explicitSignerData);
                const tmClient = yield this.getTmClient();
                const signingClient = new CarbonSigningClient_1.CarbonSigningClient(tmClient, signer);
                const signedTx = yield signingClient.sign(granteeAddress, msgExecMessages, fee, memo, signerData, this.bech32Address);
                this.txDispatchManager.enqueue(Object.assign(Object.assign({}, txRequest), { reattempts,
                    signedTx, signOpts: _signOpts, handler: { resolve, reject, requestId: `${sequence}` } }));
            }
            catch (error) {
                reject(error);
            }
        });
    }
    getBroadcastFunc(broadcastMode) {
        switch (broadcastMode) {
            case tx_2.BroadcastTxMode.BroadcastTxSync: return this.broadcastTxToMempoolWithoutConfirm.bind(this);
            case tx_2.BroadcastTxMode.BroadcastTxAsync: return this.broadcastTxWithoutConfirm.bind(this);
        }
        return this.broadcastTx.bind(this);
    }
    dispatchTx(txRequest) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            const { broadcastOpts, signedTx, handler: { resolve, reject }, } = txRequest;
            const broadcastMode = (_a = broadcastOpts === null || broadcastOpts === void 0 ? void 0 : broadcastOpts.mode) !== null && _a !== void 0 ? _a : this.txDefaultBroadCastMode;
            const broadcastFunc = this.getBroadcastFunc(broadcastMode);
            try {
                const result = yield broadcastFunc(signedTx, broadcastOpts);
                resolve(result);
            }
            catch (error) {
                const reattempts = (_b = txRequest.reattempts) !== null && _b !== void 0 ? _b : 0;
                // retry sendTx if nonce error once.
                if (!this.disableRetryOnSequenceError && reattempts < 1 && this.isNonceMismatchError(error)) {
                    // invalidate account sequence for reload on next signTx call
                    this.sequenceInvalidated = true;
                    // requeue transaction for signTx
                    this.txSignManager.enqueue({
                        reattempts: (reattempts !== null && reattempts !== void 0 ? reattempts : 0) + 1,
                        signerAddress: txRequest.signerAddress,
                        messages: txRequest.messages,
                        broadcastOpts,
                        signOpts: txRequest.signOpts,
                        handler: { resolve, reject },
                    });
                }
                else {
                    reject(error);
                }
            }
        });
    }
    sendTxs(msgs, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.triggerMerge || (opts === null || opts === void 0 ? void 0 : opts.triggerMerge)) {
                yield this.sendInitialMergeAccountTx(msgs, opts);
            }
            try {
                const result = yield this.signAndBroadcast(msgs, opts);
                if (msgs[0].typeUrl === util_1.CarbonTx.Types.MsgMergeAccount) {
                    this.updateMergeAccountStatus();
                }
                yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onBroadcastTxSuccess) === null || _a === void 0 ? void 0 : _a.call(this, msgs); });
                return result;
            }
            catch (error) {
                yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onBroadcastTxFail) === null || _a === void 0 ? void 0 : _a.call(this, msgs); });
                throw error;
            }
        });
    }
    sendInitialMergeAccountTx(msgs, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            let msg;
            yield this.reloadMergeAccountStatus();
            if (!this.accountMerged && msgs[0].typeUrl !== util_1.CarbonTx.Types.MsgMergeAccount) {
                const accountInfo = yield this.reloadAccountInfo();
                if (!accountInfo) {
                    throw new Error('Account not found!');
                }
                const { address, sequence, accountNumber } = accountInfo;
                try {
                    msg = {
                        typeUrl: util_1.CarbonTx.Types.MsgMergeAccount,
                        value: CarbonSDK_1.Carbon.Evmmerge.MsgMergeAccount.fromPartial({
                            creator: address,
                            pubKey: this.publicKey.toString('hex'),
                        }),
                    };
                    const modifiedOpts = Object.assign(Object.assign({}, opts), { sequence,
                        accountNumber });
                    yield this.signAndBroadcast([msg], modifiedOpts, { mode: tx_2.BroadcastTxMode.BroadcastTxBlock });
                    this.updateMergeAccountStatus();
                    yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onBroadcastTxSuccess) === null || _a === void 0 ? void 0 : _a.call(this, [msg]); });
                }
                catch (error) {
                    yield util_1.GenericUtils.callIgnoreError(() => { var _a; return (_a = this.onBroadcastTxFail) === null || _a === void 0 ? void 0 : _a.call(this, [msg]); });
                    throw error;
                }
            }
        });
    }
    sendTxsWithoutConfirm(msgs, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.reloadMergeAccountStatus();
            if (this.triggerMerge || (opts === null || opts === void 0 ? void 0 : opts.triggerMerge)) {
                yield this.sendInitialMergeAccountTx(msgs, opts);
            }
            const result = yield this.signAndBroadcast(msgs, opts, { mode: tx_2.BroadcastTxMode.BroadcastTxSync });
            return result;
        });
    }
    sendTx(msg, opts) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.sendTxs([msg], opts);
        });
    }
    waitForTx(txHash, throwIfNotIncludedInBlock = false, timeoutMs = 60000, pollIntervalMs = 100) {
        return __awaiter(this, void 0, void 0, function* () {
            const txId = txHash.toUpperCase();
            let timedOut = false;
            const txPollTimeout = setTimeout(() => {
                timedOut = true;
            }, timeoutMs);
            const pollForTx = (txId) => __awaiter(this, void 0, void 0, function* () {
                try {
                    if (timedOut) {
                        throw new stargate_1.TimeoutError(`Transaction with ID ${txId} was submitted but was not yet found on the chain. You might want to check later. There was a wait of ${timeoutMs / 1000} seconds.`, txId);
                    }
                    const hash = Uint8Array.from(Buffer.from(txId, 'hex'));
                    const tmClient = yield this.getTmClient();
                    const response = yield tmClient.tx({ hash });
                    const { result } = response;
                    const isDeliverTxFailure = result.code !== 0;
                    if (isDeliverTxFailure && throwIfNotIncludedInBlock)
                        throw new tx_2.CarbonCustomError(`[${result.code}] ${result.log}`, tx_2.ErrorType.BLOCK_FAIL, response);
                    return response;
                }
                catch (err) {
                    const error = err;
                    if (this.isTxHashNotFound(error, txId)) {
                        yield utils_1.sleep(pollIntervalMs);
                        return pollForTx(txId);
                    }
                    throw err;
                }
            });
            return new Promise((resolve, reject) => pollForTx(txId).then((value) => {
                clearTimeout(txPollTimeout);
                resolve(value);
            }, (error) => {
                clearTimeout(txPollTimeout);
                reject(error);
            }));
        });
    }
    getSigningClient() {
        return __awaiter(this, void 0, void 0, function* () {
            const tmClient = yield this.getTmClient();
            if (!this.signingClient) {
                this.signingClient = new CarbonSigningClient_1.CarbonSigningClient(tmClient, this.signer);
            }
            return this.signingClient;
        });
    }
    getTimeoutHeight() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const cacheBuster = ~~(new Date().getTime() / 1000);
                const response = yield fetch_1.fetch(`${this.networkConfig.tmRpcUrl}/blockchain?cache=${cacheBuster}`);
                const body = yield response.json();
                if (!((_a = body.result) === null || _a === void 0 ? void 0 : _a.last_height))
                    return undefined;
                return number_1.bnOrZero((_b = body.result) === null || _b === void 0 ? void 0 : _b.last_height).plus(this.defaultTimeoutBlocks).toNumber();
            }
            catch (error) {
                return undefined;
            }
        });
    }
    getTmClient(reconnect = false) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.tmClient || reconnect) {
                this.tmClient = yield tendermint_rpc_1.Tendermint37Client.connect(this.networkConfig.tmRpcUrl);
                const status = yield this.tmClient.status();
                this.chainId = status.nodeInfo.network;
                this.evmChainId = constant_1.CarbonEvmChainIDs[this.network];
            }
            return this.tmClient;
        });
    }
    getChainId() {
        if (!this.chainId)
            throw new Error("CarbonWallet is not initialized");
        return this.chainId;
    }
    getEvmChainId() {
        if (!this.evmChainId)
            throw new Error("CarbonWallet is not initialized");
        return this.evmChainId;
    }
    isSigner(signerType) {
        return this.signer.type === signerType;
    }
    isLedgerSigner() {
        return this.isSigner(CarbonSigner_1.CarbonSignerTypes.Ledger) || this.isLedgerViaBrowserExtension();
    }
    isLedgerViaBrowserExtension() {
        return (this.providerAgent === walletProvider_1.ProviderAgent.KeplrExtension || this.providerAgent === walletProvider_1.ProviderAgent.LeapExtension) && !proto_signing_1.isOfflineDirectSigner(this.signer);
    }
    isPrivateKeySigner() {
        return this.isSigner(CarbonSigner_1.CarbonSignerTypes.PrivateKey);
    }
    isBrowserInjectedSigner() {
        return this.isSigner(CarbonSigner_1.CarbonSignerTypes.BrowserInjected);
    }
    isSmartWalletEnabled() {
        return !!(this.mnemonic || this.privateKey);
    }
    getSmartWalletPrivateKey(blockchain = blockchain_1.SmartWalletBlockchain.Ethereum) {
        if (this.mnemonic) {
            switch (blockchain) {
                case blockchain_1.SmartWalletBlockchain.Ethereum:
                case blockchain_1.SmartWalletBlockchain.Arbitrum:
                case blockchain_1.SmartWalletBlockchain.BinanceSmartChain:
                case blockchain_1.SmartWalletBlockchain.Polygon:
                    return address_1.ETHAddress.mnemonicToPrivateKey(this.mnemonic);
                case blockchain_1.SmartWalletBlockchain.Neo:
                    return address_1.NEOAddress.mnemonicToPrivateKey(this.mnemonic);
                default:
                    return null;
            }
        }
        if (this.privateKey)
            return this.privateKey;
        return null;
    }
    reconnectTmClient(fallbackConfig) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.getTmClient(true);
            }
            catch (err) {
                const errorTyped = err;
                if (errorTyped.message.includes("Failed to fetch") && fallbackConfig) {
                    this.chainId = fallbackConfig.chainId;
                    this.evmChainId = fallbackConfig.evmChainId;
                }
            }
        });
    }
    reloadAccountInfo() {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            // carbon account always takes priority
            let evmBech32Acc = null;
            const bech32Acc = this.bech32Address && (yield this.getAccount(this.bech32Address));
            if (!bech32Acc && this.evmBech32Address) {
                evmBech32Acc = yield this.getAccount(this.evmBech32Address);
            }
            return (_a = bech32Acc !== null && bech32Acc !== void 0 ? bech32Acc : evmBech32Acc) !== null && _a !== void 0 ? _a : undefined;
        });
    }
    getAccount(queryAddress) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const account = (yield this.getQueryClient().auth.Account({ address: queryAddress })).account;
                if (!account)
                    return;
                const { accountNumber, sequence, address } = auth_1.BaseAccount.decode(account.value);
                return {
                    address,
                    accountNumber: accountNumber.toNumber(),
                    sequence: sequence.toNumber(),
                };
            }
            catch (error) {
                if (!this.isAccountNotFoundError(error, queryAddress))
                    throw error;
            }
            return;
        });
    }
    reloadAccountSequence() {
        var _a, _b, _c, _d, _e;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.sequenceInvalidated)
                this.sequenceInvalidated = false;
            const info = yield this.reloadAccountInfo();
            const pubkey = (_b = (_a = this.accountInfo) === null || _a === void 0 ? void 0 : _a.pubkey) !== null && _b !== void 0 ? _b : {
                type: "tendermint/PubKeySecp256k1",
                value: this.publicKey.toString("base64"),
            };
            const chainId = (_e = (_d = (_c = this.accountInfo) === null || _c === void 0 ? void 0 : _c.chainId) !== null && _d !== void 0 ? _d : this.chainId) !== null && _e !== void 0 ? _e : (yield this.getQueryClient().chain.getChainId());
            if (info) {
                this.accountInfo = Object.assign(Object.assign({}, info), { pubkey,
                    chainId });
            }
        });
    }
    reloadMergeAccountStatus() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.accountMerged)
                return;
            const queryClient = this.getQueryClient();
            const response = yield queryClient.evmmerge.MappedAddress({ address: this.bech32Address });
            this.accountMerged = !!(response === null || response === void 0 ? void 0 : response.mappedAddress);
            this.sequenceInvalidated = true;
        });
    }
    updateMergeAccountStatus() {
        this.accountMerged = true;
        // invalidate sequence after merging
        this.sequenceInvalidated = true;
    }
    isEvmWallet() {
        return this.providerAgent === walletProvider_1.ProviderAgent.MetamaskExtension;
    }
    estimateTxFee(messages, feeDenom = constant_1.DEFAULT_FEE_DENOM) {
        var _a;
        const denomGasPrice = (_a = this.gasFee) === null || _a === void 0 ? void 0 : _a.getGasPrice(feeDenom);
        let totalGasCost = messages.reduce((result, message) => {
            var _a;
            const gasCost = (_a = this.gasFee) === null || _a === void 0 ? void 0 : _a.getGasCost(message.typeUrl);
            return result.plus(gasCost !== null && gasCost !== void 0 ? gasCost : number_1.BN_ZERO);
        }, number_1.BN_ZERO);
        let totalFees = totalGasCost.times(denomGasPrice !== null && denomGasPrice !== void 0 ? denomGasPrice : number_1.BN_ZERO);
        // override zero gas cost tx with some gas for tx execution
        // set overall fee to zero, implying 0 gas price.
        if (totalGasCost.isZero()) {
            totalGasCost = constant_1.DEFAULT_GAS;
            totalFees = number_1.BN_ZERO;
        }
        return {
            amount: [
                {
                    amount: totalFees.toString(10),
                    denom: feeDenom,
                },
            ],
            gas: totalGasCost.toString(10),
        };
    }
    getQueryClient() {
        if (!this.query)
            throw new Error("wallet not initialized");
        return this.query;
    }
}
exports.CarbonWallet = CarbonWallet;
(function (CarbonWallet) {
    CarbonWallet.TxRaw = Object.assign({}, tx_3.TxRaw);
})(CarbonWallet = exports.CarbonWallet || (exports.CarbonWallet = {}));
